import { memo, useCallback, useRef, useState, type ChangeEvent } from 'react';
import { Tooltip } from '@/components/Tooltip';
import { ConfirmModal } from '@/components/ConfirmModal';
import { slugLabel } from '../builder/schema-draft';
import type { SectionDraft } from '../builder/schema-draft';
import type { UseSchemaDraftResult } from '../builder/useSchemaDraft';

export interface WysiwygSectionInspectorProps {
  section: SectionDraft;
  sectionIndex: number;
  totalSections: number;
  draft: UseSchemaDraftResult;
  onClose: () => void;
}

/**
 * Right-side inspector for a selected section. Title, sidebar toggle,
 * Move / Add-field / Delete actions, and section ID under Advanced.
 *
 * Same layout primitives as the field inspector (`kstc-form-group`,
 * `kstc-label`, `kstc-input`, `kstc-btn` variants) so the two side panels
 * read as one consistent surface.
 */
export const WysiwygSectionInspector = memo(function WysiwygSectionInspector({
  section,
  sectionIndex,
  totalSections,
  draft,
  onClose,
}: WysiwygSectionInspectorProps) {
  const idInputRef = useRef<HTMLInputElement>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleTitleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      draft.updateSection(section.uiId, { title: e.target.value });
    },
    [draft, section.uiId],
  );

  const handleSidebarToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      draft.updateSection(section.uiId, { sidebar: e.target.checked || undefined });
    },
    [draft, section.uiId],
  );

  const handleIdChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      draft.updateSection(section.uiId, { id: e.target.value, idLocked: true });
    },
    [draft, section.uiId],
  );

  const handleIdToggle = useCallback(() => {
    if (section.idLocked) {
      draft.updateSection(section.uiId, { id: slugLabel(section.title), idLocked: false });
    } else {
      draft.updateSection(section.uiId, { idLocked: true });
      setTimeout(() => idInputRef.current?.focus(), 0);
    }
  }, [draft, section.uiId, section.idLocked, section.title]);

  const handleMoveUp = useCallback(() => {
    draft.moveSection(section.uiId, -1);
  }, [draft, section.uiId]);
  const handleMoveDown = useCallback(() => {
    draft.moveSection(section.uiId, 1);
  }, [draft, section.uiId]);
  const handleDelete = useCallback(() => {
    setConfirmDeleteOpen(true);
  }, []);
  const handleConfirmDelete = useCallback(() => {
    setConfirmDeleteOpen(false);
    draft.removeSection(section.uiId);
    onClose();
  }, [draft, section.uiId, onClose]);
  const handleCancelDelete = useCallback(() => {
    setConfirmDeleteOpen(false);
  }, []);

  const handleAddField = useCallback(() => {
    draft.addField(section.uiId, 'text');
  }, [draft, section.uiId]);

  return (
    <aside className="wysiwyg-inspector" role="dialog" aria-label="Section details">
      <div className="wysiwyg-inspector-header">
        <span className="wysiwyg-inspector-kind">Section</span>
        <span className="wysiwyg-inspector-title">{section.title || 'untitled'}</span>
        <button
          type="button"
          className="wysiwyg-inspector-close"
          onClick={onClose}
          aria-label="Close"
        >×</button>
      </div>

      <div className="wysiwyg-inspector-actions">
        <button
          type="button"
          className="kstc-btn kstc-btn--secondary kstc-btn--sm"
          onClick={handleMoveUp}
          disabled={sectionIndex === 0}
          title="Move this section above the one before it"
        >↑ Move up</button>
        <button
          type="button"
          className="kstc-btn kstc-btn--secondary kstc-btn--sm"
          onClick={handleMoveDown}
          disabled={sectionIndex === totalSections - 1}
          title="Move this section below the one after it"
        >↓ Move down</button>
        <button
          type="button"
          className="kstc-btn kstc-btn--secondary kstc-btn--sm"
          onClick={handleAddField}
          title="Add a new field at the end of this section"
        >+ Add field</button>
        <button
          type="button"
          className="kstc-btn kstc-btn--danger kstc-btn--sm"
          onClick={handleDelete}
          title="Remove this section and all its fields from the form"
        >Delete</button>
      </div>

      <div className="wysiwyg-inspector-body">
        <div className="kstc-form-group">
          <label className="kstc-label" htmlFor={`wysiwyg-insp-section-title-${section.uiId}`}>Title</label>
          <input
            id={`wysiwyg-insp-section-title-${section.uiId}`}
            type="text"
            className="kstc-input"
            value={section.title}
            onChange={handleTitleChange}
            placeholder="Section title"
          />
        </div>

        <div className="kstc-form-group">
          <label className="wysiwyg-inspector-checkbox">
            <input
              type="checkbox"
              checked={!!section.sidebar}
              onChange={handleSidebarToggle}
            />
            {' '}Show as a side panel on wider screens{' '}
            <Tooltip
              content="On laptops and desktops this section appears as a narrow card on the right of the form, which is handy for short summary cards like billing totals. On phones and tablets it stacks below the main form."
              position="top"
            />
          </label>
        </div>

        <details className="wysiwyg-inspector-advanced">
          <summary className="wysiwyg-inspector-advanced-summary">Advanced</summary>
          <div className="wysiwyg-inspector-advanced-body">
            <div className="kstc-form-group">
              <label className="kstc-label">
                Internal name{' '}
                <Tooltip content="The system name used to identify this section. Auto-generated from the title and rarely needs to change." position="top" />
              </label>
              <div className="wysiwyg-inspector-key-row">
                <input
                  ref={idInputRef}
                  type="text"
                  className="kstc-input"
                  value={section.id}
                  onChange={handleIdChange}
                  placeholder="Generated from the title"
                  readOnly={!section.idLocked}
                  aria-readonly={!section.idLocked}
                />
                <button
                  type="button"
                  className="kstc-btn kstc-btn--secondary kstc-btn--sm"
                  onClick={handleIdToggle}
                  title={section.idLocked
                    ? 'Stop using a custom internal name and let it follow the title again.'
                    : 'Set a custom internal name (it will stop following the title).'}
                >
                  {section.idLocked ? 'Use auto' : 'Customize'}
                </button>
              </div>
              <div className="wysiwyg-inspector-key-mode">
                {section.idLocked
                  ? 'Custom. Set independently of the title.'
                  : 'Auto. Kept in sync with the title.'}
              </div>
            </div>
          </div>
        </details>
      </div>
      <ConfirmModal
        open={confirmDeleteOpen}
        title="Remove section?"
        message={`Remove section "${section.title || 'untitled'}"? Its ${section.fields.length} field${section.fields.length === 1 ? '' : 's'} will be removed too.`}
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </aside>
  );
});
